import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAlunoContext } from '@/contexts/AlunoContext';
import { getDefaultRoute } from '@/utils/getDefaultRoute';
import { Loading } from '@/components/ui/loading';

interface AlunoGuardProps {
  children: React.ReactNode;
}

/**
 * Guard que protege rotas específicas para alunos
 * Verifica se o usuário tem role ALUNO e valida acesso aos dados
 */
export function AlunoGuard({ children }: AlunoGuardProps) {
  const { hasRole, isSuperAdmin, isLoading: authLoading, user } = useAuth();
  const { aluno, isLoading: alunoLoading } = useAlunoContext();

  // Aguardar carregamento da autenticação
  if (authLoading || alunoLoading) {
    return <Loading size="lg" message="Verificando permissões..." />;
  }

  // Super admin tem acesso (para testes/depuração)
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Verificar se tem role ALUNO
  if (!hasRole('ALUNO')) {
    // Redirecionar para rota padrão baseada no role do usuário
    const defaultRoute = getDefaultRoute(user);
    return <Navigate to={defaultRoute} replace />;
  }

  // Se não encontrou dados do aluno vinculado ao usuário
  if (!aluno) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-muted-foreground text-center">
          Nenhum registro de aluno encontrado para este usuário.
        </p>
        <p className="text-sm text-muted-foreground text-center">
          Entre em contato com a secretaria da escola.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
